import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasRole, hasPermission, ROLES } from '../utils/roleUtils';

interface RoleGuardProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  fallback?: React.ReactNode;
  requireAll?: boolean; // 如果為 true，需要同時滿足角色和權限
}

const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallback = null,
  requireAll = false
}) => {
  const { user } = useAuth();

  if (!user) {
    return <>{fallback}</>;
  }

  let hasAccess = true;

  // 檢查角色權限
  if (requiredRole) {
    hasAccess = hasRole(user.roleId, requiredRole);
  }

  // 檢查特定權限
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(user.roleId, requiredPermission);

    if (requireAll) {
      hasAccess = hasAccess && hasRequiredPermission;
    } else {
      hasAccess = hasAccess || hasRequiredPermission;
    }
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default RoleGuard;

// 便捷的 HOC 組件
export const withRoleGuard = (
  WrappedComponent: React.ComponentType<any>,
  requiredRole?: string,
  requiredPermission?: string,
  fallback?: React.ReactNode
) => {
  return (props: any) => (
    <RoleGuard
      requiredRole={requiredRole}
      requiredPermission={requiredPermission}
      fallback={fallback}
    >
      <WrappedComponent {...props} />
    </RoleGuard>
  );
};

// 預定義的角色守衛組件
export const AdminOnly: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback = null
}) => (
  <RoleGuard requiredRole={ROLES.ADMIN} fallback={fallback}>
    {children}
  </RoleGuard>
);

export const ManagerOrAbove: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback = null
}) => (
  <RoleGuard requiredRole={ROLES.MANAGER} fallback={fallback}>
    {children}
  </RoleGuard>
);

export const ReaderOrAbove: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback = null
}) => (
  <RoleGuard requiredRole={ROLES.READER} fallback={fallback}>
    {children}
  </RoleGuard>
);
